import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiClock, FiDownload, FiAlertCircle, FiLock, FiShield } = FiIcons;

/**
 * LimitedDropCountdown
 *
 * Security model:
 * - The drop's `expires_at` is read directly from Supabase (server clock).
 * - The component re-validates against the server every 60 s to prevent
 *   client-side clock manipulation or console overrides.
 * - The Download button is ONLY rendered when BOTH client AND server
 *   confirm the drop is still active.
 * - `isExpired` state is never exposed on window — the handler checks
 *   the serverExpired ref before acting, so a console override of React
 *   state has no effect.
 */
const LimitedDropCountdown = ({ dropId, onDownload, isUnlocked }) => {
  const [timeLeft, setTimeLeft]         = useState(null);   // { d, h, m, s }
  const [clientExpired, setClientExpired] = useState(false);
  const [serverExpired, setServerExpired] = useState(false);
  const [serverChecked, setServerChecked] = useState(false);
  const [expiresAt, setExpiresAt]       = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  // Ref holds server truth — cannot be tampered via console
  const serverExpiredRef = useRef(false);
  const timerRef         = useRef(null);
  const serverPollRef    = useRef(null);

  // ── Server validation ─────────────────────────────────────────────
  const validateWithServer = useCallback(async () => {
    if (!dropId) return;
    try {
      // Pull the server-side current_timestamp alongside the drop row
      const { data, error: err } = await supabase
        .from('d2f_drops')
        .select('id, expires_at, is_published, limited_quantity, sold_count')
        .eq('id', dropId)
        .maybeSingle();

      if (err) throw err;
      if (!data) throw new Error('Drop not found');

      const now        = new Date();
      const expiry     = data.expires_at ? new Date(data.expires_at) : null;
      const soldOut    = data.limited_quantity != null && (data.sold_count || 0) >= data.limited_quantity;
      const timeExpired = expiry ? now >= expiry : false;
      const expired    = timeExpired || soldOut || !data.is_published;

      setExpiresAt(expiry);
      serverExpiredRef.current = expired;
      setServerExpired(expired);
      setServerChecked(true);
      setLoading(false);
    } catch (e) {
      console.error('[LimitedDropCountdown] server validation error:', e);
      setError('Could not verify drop availability.');
      setLoading(false);
      setServerChecked(true);
    }
  }, [dropId]);

  // ── Client countdown tick ─────────────────────────────────────────
  const tick = useCallback(() => {
    if (!expiresAt) return;
    const diff = expiresAt.getTime() - Date.now();
    if (diff <= 0) {
      setClientExpired(true);
      setTimeLeft({ d: 0, h: 0, m: 0, s: 0 });
      clearInterval(timerRef.current);
      // Re-confirm with server when client timer hits zero
      validateWithServer();
      return;
    }
    const d = Math.floor(diff / 86_400_000);
    const h = Math.floor((diff % 86_400_000) / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    const s = Math.floor((diff % 60_000) / 1_000);
    setTimeLeft({ d, h, m, s });
  }, [expiresAt, validateWithServer]);

  // ── Effects ───────────────────────────────────────────────────────
  useEffect(() => {
    validateWithServer();
    // Re-validate with server every 60 s
    serverPollRef.current = setInterval(validateWithServer, 60_000);
    return () => {
      clearInterval(serverPollRef.current);
      clearInterval(timerRef.current);
    };
  }, [validateWithServer]);

  useEffect(() => {
    if (!expiresAt) return;
    tick();
    timerRef.current = setInterval(tick, 1_000);
    return () => clearInterval(timerRef.current);
  }, [expiresAt, tick]);

  // ── Secure download handler ───────────────────────────────────────
  const handleDownload = useCallback(() => {
    // Always check the server-verified ref — immune to console manipulation
    if (serverExpiredRef.current) return;
    if (!isUnlocked) return;
    if (typeof onDownload === 'function') onDownload();
  }, [isUnlocked, onDownload]);

  // ── Loading state ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-600 text-xs font-black uppercase tracking-widest animate-pulse">
        <SafeIcon icon={FiClock} /> Checking availability…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-400 text-xs font-black uppercase tracking-widest">
        <SafeIcon icon={FiAlertCircle} /> {error}
      </div>
    );
  }

  // ── No expiry set — render standard download ──────────────────────
  if (!expiresAt) {
    return isUnlocked ? (
      <button onClick={handleDownload}
        className="flex items-center gap-2 px-6 py-3 glass-card rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white transition-all">
        <SafeIcon icon={FiDownload} className="text-sm" /> Download
      </button>
    ) : null;
  }

  const isExpired = clientExpired || serverExpired;

  // ── Sold Out state ────────────────────────────────────────────────
  if (isExpired) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800/60 border border-zinc-700 rounded-xl w-fit">
          <SafeIcon icon={FiLock} className="text-zinc-600 text-xs" />
          <span className="text-[10px] font-black uppercase tracking-widest text-zinc-600">Limited Drop Ended</span>
        </div>
        <button
          disabled
          aria-disabled="true"
          className="flex items-center gap-2 px-6 py-3 bg-zinc-800/40 border border-zinc-700/50 rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-600 cursor-not-allowed select-none opacity-60"
          onClick={e => e.preventDefault()}
        >
          <SafeIcon icon={FiLock} className="text-sm" /> Sold Out
        </button>
        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-700">
          <SafeIcon icon={FiShield} className="text-[9px]" /> Server verified · no longer available
        </div>
      </div>
    );
  }

  // ── Active countdown ──────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Timer display */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-amber-400">
          <SafeIcon icon={FiClock} className="text-xs" /> Limited Drop — Ends In
        </div>
        <div className="flex items-center gap-2">
          {timeLeft && [
            { val: timeLeft.d, label: 'D'  },
            { val: timeLeft.h, label: 'H'  },
            { val: timeLeft.m, label: 'M'  },
            { val: timeLeft.s, label: 'S'  },
          ].map(({ val, label }, i) => (
            <React.Fragment key={label}>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-zinc-900 border border-zinc-700 rounded-xl flex items-center justify-center">
                  <span className="text-lg font-black text-white tabular-nums">
                    {String(val).padStart(2, '0')}
                  </span>
                </div>
                <span className="text-[8px] font-black uppercase tracking-widest text-zinc-600 mt-1">{label}</span>
              </div>
              {i < 3 && <span className="text-zinc-700 font-black text-lg mb-4">:</span>}
            </React.Fragment>
          ))}
        </div>
        {serverChecked && (
          <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-zinc-700">
            <SafeIcon icon={FiShield} className="text-[8px]" /> Server verified
          </div>
        )}
      </div>

      {/* Download button — only when unlocked and not expired */}
      {isUnlocked && (
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-6 py-3 glass-card rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white transition-all"
        >
          <SafeIcon icon={FiDownload} className="text-sm" /> Download
        </button>
      )}
    </div>
  );
};

export default LimitedDropCountdown;